// =============================================================================
// PROJECT DATA
// Single source of truth for all project information used across the site.
//
// CASE STUDY IMAGES — just drop files into public/case-studies/{project-id}/
// with standardized names. They load automatically. No imports needed.
//
// Expected filenames (positional, in grid order):
//   block-1.jpg, block-2.jpg, block-3.jpg, block-4.jpg, block-5.jpg,
//   block-6.png (logo), block-7.jpg, block-8.jpg, block-9.jpg
//
// See: src/images/case-studies/NAMING-CONVENTION.md
// =============================================================================

// Thumbnails / hero images (these still need imports for webpack processing)
import AuburnRoadMain from "../images/case-studies/auburn-road/arv.png";
import AuburnRoadThumb from "../images/case-studies/auburn-road/arv-main.png";
import CUACCards from "../images/cuac-card-image@2x-100.jpg";
import MillennialBodyImageArt from "../images/case-studies/millennial-body-image/trap-bright.png";
import NightToShineThumb from "../images/case-studies/night-to-shine/nts.jpg";

const projects = [
  {
    id: "auburn-road",
    title: "Auburn Road Vineyards",
    category: "Branding + Website",
    outlineStyle: "gradient-pink",
    meta: "Branding • Web design • Development • CMS",
    thumb: AuburnRoadThumb,
    heroImage: AuburnRoadMain,
    shortDesc:
      "A ground-up brand redesign featuring original artwork by a local artist, hand-painted for each wine. Brought to life through label design, packaging, and a new website.",
    summary:
      "A complete brand redesign from the ground up. We partnered with a local artist who hand-painted original artwork for each wine, giving every bottle its own identity. That custom art became the foundation for the labels, packaging, and broader brand — colors, logos, stationery, and a fully designed and developed website.",
    services: [
      "Brand strategy",
      "Logo design",
      "Color palette",
      "Custom illustration",
      "Label & package design",
      "Stationery",
      "Web design",
      "Web development",
    ],
    outcomes: [
      "Cohesive brand identity built from the ground up",
      "Original hand-painted artwork giving each wine its own identity",
      "Stronger shelf presence with new label and packaging",
      "Modern, mobile-friendly website",
    ],
    caseStudy: {
      gridBg: "#f8f1e5",
      colors: [
        { name: "Primary", value: "#673468" },
        { name: "Secondary", value: "#f8f1e5" },
        { name: "Accent", value: "#444444" },
        { name: "Text", value: "#ffffff" },
      ],
    },
    featured: true,
  },
  {
    id: "cuac",
    title: "Cooking up a Cure",
    category: "Branding + Design",
    outlineStyle: "black",
    meta: "Branding • Graphic design",
    thumb: CUACCards,
    heroImage: CUACCards,
    shortDesc:
      "Brand and design work built for consistency across print and digital touchpoints.",
    summary:
      "Branding and graphic design work built for consistency across print and digital touchpoints.",
    services: ["Branding", "Graphic design"],
    outcomes: [
      "Cohesive visual system across collateral",
      "Clearer, more consistent brand presence",
    ],
    caseStudy: {
      gridBg: "#E8E8ED",
      colors: [
        { name: "Primary", value: "#2B525B" },
        { name: "Secondary", value: "#FEC34A" },
        { name: "Accent", value: "#ffffff" },
        { name: "Text", value: "#ffffff" },
      ],
    },
    featured: true,
  },
  {
    id: "millennial-body-image-podcast",
    title: "Millennial Body Image Podcast",
    category: "Branding + Design",
    outlineStyle: "blue-purple",
    meta: "Branding • Graphic design",
    thumb: MillennialBodyImageArt,
    heroImage: MillennialBodyImageArt,
    shortDesc:
      "Visual identity and graphic design that keeps the brand consistent across episodes and platforms.",
    summary:
      "Branding and graphic design built to keep the show's visual identity cohesive across platforms.",
    services: ["Branding", "Graphic design"],
    outcomes: [
      "Consistent visual identity",
      "Clearer, more recognizable brand",
    ],
    caseStudy: {
      gridBg: "#E8E8ED",
      colors: [
        { name: "Primary", value: "#e67ab0" },
        { name: "Secondary", value: "#404268" },
        { name: "Accent", value: "#ccd3ec" },
        { name: "Text", value: "#ffffff" },
      ],
    },
    featured: false,
  },
  {
    id: "night-to-shine",
    title: "Night to Shine",
    category: "Web design + Development",
    outlineStyle: "black",
    meta: "Web design • Web development • Hosting & DNS",
    thumb: NightToShineThumb,
    heroImage: NightToShineThumb,
    shortDesc:
      "Updated website and hosting setup that makes it easy to manage event details and registrations year after year.",
    summary:
      "A refreshed Night to Shine website with clean information architecture, responsive layouts, and a reliable hosting and DNS setup that keeps event information easy to find and simple to update.",
    services: [
      "Web design",
      "Web development",
      "Hosting & DNS setup",
    ],
    outcomes: [
      "Modern, responsive event website",
      "Simpler updates for annual event details",
      "Stable hosting and DNS configuration",
    ],
    caseStudy: {
      gridBg: "#E8E8ED",
      colors: [
        { name: "Primary", value: "#ffffff" },
        { name: "Secondary", value: "#ffffff" },
        { name: "Accent", value: "#ffffff" },
        { name: "Text", value: "#ffffff" },
      ],
    },
    featured: true,
  },
];

export default projects;

// Helper: get only featured projects
export const getFeaturedProjects = () => projects.filter((p) => p.featured);

// Helper: find a project by ID
export const getProjectById = (id) => projects.find((p) => p.id === id) || null;
